import { type FormEvent, useState } from 'react';
import { useApi } from '../lib/use-api';
import { api } from '../lib/api';
import { Empty, ErrorBanner, Loading, Panel } from '../components/ui';

interface Integrator {
  id: string;
  name: string;
  acquisition_mode: 'invoiced' | 'consignment';
  discount_tier: string;
  devices_held: number;
  is_active: boolean;
}

/** Wireframe C4 — System Integrator management (FR-C10, FR-C11). */
export function Integrators() {
  const integrators = useApi<{ items: Integrator[] }>('/internal/system-integrators');
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [mode, setMode] = useState<'invoiced' | 'consignment'>('invoiced');
  const [discount, setDiscount] = useState('20');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/internal/system-integrators', {
        name,
        acquisitionMode: mode,
        discountTier: Number(discount),
      });
      setName('');
      setShowForm(false);
      integrators.reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>System integrators</h1>
          <p>Acquisition mode, discount tier and devices currently held.</p>
        </div>
        <button onClick={() => setShowForm((open) => !open)}>{showForm ? 'Cancel' : '+ Add integrator'}</button>
      </div>

      <ErrorBanner error={error ?? integrators.error} />

      {showForm ? (
        <Panel title="New system integrator">
          <form className="panel-body" onSubmit={submit}>
            <div className="filters">
              <div className="field" style={{ flex: 2 }}>
                <label htmlFor="name">Name</label>
                <input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="field">
                <label htmlFor="mode">Acquisition mode</label>
                <select id="mode" value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
                  <option value="invoiced">Invoiced sale</option>
                  <option value="consignment">Transfer / consignment</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="discount">Discount %</label>
                <input
                  id="discount"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                />
              </div>
              <button type="submit" disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
            <p className="muted" style={{ margin: 0, fontSize: 12 }}>
              Invoiced sales earn the maximum discount and are billed upfront. Consignment transfers earn a smaller
              discount and are not billed until sold on (FR-C10).
            </p>
          </form>
        </Panel>
      ) : null}

      <section className="panel">
        {integrators.loading ? (
          <Loading />
        ) : integrators.data?.items.length ? (
          <table>
            <thead>
              <tr>
                <th>System integrator</th>
                <th>Acquisition mode</th>
                <th className="num">Discount</th>
                <th className="num">Devices held</th>
              </tr>
            </thead>
            <tbody>
              {integrators.data.items.map((si) => (
                <tr key={si.id}>
                  <td>{si.name}</td>
                  <td>
                    <span className={`badge ${si.acquisition_mode === 'invoiced' ? 'info' : ''}`}>
                      {si.acquisition_mode === 'invoiced' ? 'Invoiced' : 'Consignment'}
                    </span>
                  </td>
                  <td className="num">{Number(si.discount_tier)}%</td>
                  <td className="num">{si.devices_held}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No system integrators recorded.</Empty>
        )}
      </section>
    </>
  );
}
